import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { inspectionsAPI, adminAPI, validationAPI, workshopsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import DuplicateAlert from '../components/DuplicateAlert';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';
import { 
  ArrowLeft, 
  ArrowRight, 
  Loader2, 
  Plus, 
  Trash2, 
  Save, 
  CheckCircle, 
  XCircle,
  MinusCircle,
  Lock,
  User,
  Car,
  Cylinder,
  ClipboardCheck,
  Shield,
  Calendar,
  AlertTriangle,
  Upload,
  FileText,
  Truck,
  Package,
  Copy,
  CopyPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { getRecordTypeLabel, getRecordTypeEmoji, formatDate } from '../lib/utils';
import { format, addMonths, addYears, parseISO } from 'date-fns';

// Reusable test result buttons (Pass / Fail / Not Conducted)
const TestResultButtons = ({ value, onChange, testId = '' }) => {
  const options = [
    { key: 'pass', label: 'Pass', icon: CheckCircle, activeClass: 'bg-emerald-600 hover:bg-emerald-700' },
    { key: 'fail', label: 'Fail', icon: XCircle, activeClass: 'bg-red-600 hover:bg-red-700' },
    { key: 'not_conducted', label: 'Not Conducted', icon: MinusCircle, activeClass: 'bg-gray-500 hover:bg-gray-600 text-white' },
  ];
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map(({ key, label, icon: Icon, activeClass }) => (
        <Button
          key={key}
          variant={value === key ? 'default' : 'outline'}
          size="sm"
          type="button"
          onClick={() => onChange(key)}
          className={value === key ? activeClass : ''}
          data-testid={testId ? `${testId}-${key}` : undefined}
        >
          <Icon className="mr-1 h-4 w-4" /> {label}
        </Button>
      ))}
    </div>
  );
};

// Constants
const DEFAULT_REGIONS = [
  'Dar es Salaam',
  'Pwani',
  'Morogoro',
  'Mtwara',
];

const FUEL_TYPES = [
  { value: 'bi_fuel', label: 'Bi-Fuel' },
  { value: 'dedicated', label: 'Dedicated' },
  { value: 'dual', label: 'Dual Fuel' },
];

const CYLINDER_TYPES = [
  { value: 'I', label: 'Type I - All Metal' },
  { value: 'II', label: 'Type II - Metal Liner, Hoop Wrapped' },
  { value: 'III', label: 'Type III - Metal Liner, Full Wrap' },
  { value: 'IV', label: 'Type IV - Plastic Liner, Full Wrap' },
];

const CYLINDER_POSITIONS = [
  { value: 'boot_trunk', label: 'Boot / Trunk' },
  { value: 'under_chassis', label: 'Under Chassis' },
  { value: 'behind_rear_seats', label: 'Behind Rear Seats' },
  { value: 'pickup_cargo_bed', label: 'Pickup Cargo Bed' },
  { value: 'roof', label: 'Roof' },
  { value: 'side_mounted', label: 'Side-Mounted' },
  { value: 'behind_cab', label: 'Behind the Cab' },
  { value: 'under_driver_seat', label: 'Under Driver Seat' },
];

const SERVICE_PRESSURES = [
  { value: '20', label: '20 MPa' },
  { value: '25', label: '25 MPa' },
];

// Trailer-specific testing pressures
const TESTING_PRESSURES = [
  { value: '30', label: '30 MPa' },
  { value: '37.5', label: '37.5 MPa' },
];

const LIFE_SPANS = [
  { value: '15', label: '15 Years' },
  { value: '20', label: '20 Years' },
];

const CYLINDER_MANUFACTURERS = [
  'HENGYANG JINHUA',
  'ANHUI CLEAN ENERGY',
  'HENAN MINGGUAN',
  'HEBEI BAIGONG',
  'SINOMA',
  'SAHUWALA HIGH PRESSURE CYLINDER',
  'CONFIDENCE ENTERPRISES PVT LTD',
  'PICC',
  'RAMA CYLINDER',
  'AURIC GREEN SOLUTION PVT LTD',
  'SHIJIAZHUANG ENRIC GAS EQUIPMENT CO. LTD.',
];

const INSPECTION_TYPES = [
  { value: 'new_installation', label: 'New Installation' },
  { value: 'periodic', label: 'Periodic Inspection' },
  { value: 'after_accident', label: 'After Accident / Repair' },
];

const VALIDITY_PERIODS = [
  { value: '6', label: '6 Months' },
  { value: '12', label: '12 Months' },
];

const OVERALL_RESULTS = [
  { value: 'approved', label: 'Approved', color: 'bg-emerald-500' },
  { value: 'conditionally_approved', label: 'Conditionally Approved', color: 'bg-amber-500' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-500' },
];

const NGV_ORIGINS = [
  { value: 'oem', label: 'OEM', description: 'Factory-installed CNG system' },
  { value: 'retrofitted', label: 'Retrofitted', description: 'After-market CNG conversion' },
];

// CNG Storage-specific dropdowns
const FACILITY_CATEGORIES = [
  { value: 'filling_station', label: 'Filling Station' },
  { value: 'industrial_site', label: 'Industrial Site' },
  { value: 'depot', label: 'Depot' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'warehouse', label: 'Warehouse' },
];

const STORAGE_INSTALLATION_POSITIONS = [
  { value: 'horizontal_above_ground', label: 'Horizontal Above Ground' },
  { value: 'vertical_above_ground', label: 'Vertical Above Ground' },
  { value: 'containerized', label: 'Containerized' },
  { value: 'mobile_skid', label: 'Mobile Skid' },
  { value: 'underground', label: 'Underground' },
];

export default function InspectionFormPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { id: draftId } = useParams(); // Present when resuming a draft at /inspections/:id/edit
  const recordId = searchParams.get('record_id');
  const facilityTypeParam = searchParams.get('facility_type') || 'car';
  const [facilityType, setFacilityType] = useState(facilityTypeParam);

  // Check if this is a trailer or storage inspection
  const isTrailer = facilityType === 'trailer';
  const isStorage = facilityType === 'cng_storage';
  const isSpecialType = isTrailer || isStorage; // Both share similar form logic

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [config, setConfig] = useState({ max_cylinders: 16, regions: [] });
  const [isLoadingDraft, setIsLoadingDraft] = useState(!!draftId);
  
  // Form Data - Step 1: Inspection Details
  const [inspectionDetails, setInspectionDetails] = useState({
    inspection_type: 'new_installation',
    validity_months: 12,
    inspection_date: format(new Date(), 'yyyy-MM-dd'),
    ngv_origin: 'oem', // OEM or Retrofitted (NOT used for trailers/storage)
    installer_id: '', // Workshop ID for registered workshops
    installer_name: '', // Only required if retrofitted or "Other" selected
  });
  
  // Workshops for installer selection
  const [workshops, setWorkshops] = useState([]);

  // Form Data - Step 2: Vehicle & Owner Information
  // For Trailer: fuel_type->product_name, make->manufacturer, model->product_serial, 
  //              engine_number->container_number, mileage->capacity
  // For Storage: registration_number->serial_number, model->facility_category, 
  //              chassis_number->installation_position, engine_number->user_name
  const [vehicleOwnerInfo, setVehicleOwnerInfo] = useState({
    owner_name: '',
    owner_phone: '',
    owner_email: '',
    registration_number: '', // For Storage: serial_number
    vehicle_type: facilityType, // Auto-filled and locked
    fuel_type: isSpecialType ? '' : 'bi_fuel', // For trailer/storage: product_name (empty by default)
    make: '', // For trailer/storage: manufacturer
    model: '', // For trailer: product_serial; For storage: facility_category
    year: '',
    chassis_number: '', // For storage: storage_installation_position
    engine_number: '', // For trailer: container_number; For storage: user_name
    mileage: '', // For trailer/storage: capacity (m³)
    region: '',
  });

  // Form Data - Step 3: CNG Cylinder Information
  // For Trailer/Storage: water_volume is replaced with testing_pressure dropdown
  const [cylinders, setCylinders] = useState([{
    serial_number: '',
    cylinder_type: 'I',
    manufacturer: '',
    installation_position: 'boot_trunk', // NOT used for trailers/storage
    service_pressure: '20',
    water_volume: '', // For trailer/storage: replaced by testing_pressure
    testing_pressure: '30', // Trailer/Storage: 30 or 37.5 MPa
    manufacturing_date: '',
    life_span: '15',
    expiry_date: '',
    isDuplicate: false, // Track if this is a duplicated cylinder
  }]);

  // Form Data - Step 4: Inspection Results
  // For Trailer/Storage: NDT is mandatory, file uploads required for leak test, NDT, and pressure test (if conducted)
  const [inspectionResults, setInspectionResults] = useState({
    visual_inspection: null, // 'pass' | 'fail'
    gas_leak_test: null, // 'pass' | 'fail'
    pressure_test: null, // 'pass' | 'fail' | null (optional for non-trailer/storage)
    ndt_test: null, // 'pass' | 'fail' | null (optional for vehicles, MANDATORY for trailer/storage)
    overall_result: 'approved',
    notes: '',
  });

  // Test report file uploads (for Trailer inspections)
  const [testReportFiles, setTestReportFiles] = useState({
    leak_test_report: null,    ndt_test_report: null,
    pressure_test_report: null,
  });
  const [uploading, setUploading] = useState({});
  const [expiredCylinders, setExpiredCylinders] = useState([]); // Track cylinders that are expired on inspection date
  // Real-time duplicate detection for registration / storage serial number
  const [regDuplicate, setRegDuplicate] = useState(null); // { value, existing_record } | null
  const [regChecking, setRegChecking] = useState(false);
  const [cylinderDuplicates, setCylinderDuplicates] = useState([]); // [{ serial_number, existing_record, same_facility }, ...]
  // Per-cylinder duplicate info keyed by row index: { [index]: { value, inFormLocation?, existing_record?, same_facility? } }
  const [cylinderDupByRow, setCylinderDupByRow] = useState({});
  const [cylinderChecking, setCylinderChecking] = useState(false);

  // Computed values - Valid To PREVIEW (for display only before save)
  // The actual valid_to is calculated and stored by the backend
  // Uses real calendar month addition (NOT number of days)
  // Example: Oct 1, 2026 + 6 months = Apr 1, 2027
  const validTo = useMemo(() => {
    if (inspectionDetails.inspection_date && inspectionDetails.validity_months) {
      // Convert validity_months to number for addMonths
      const months = Number(inspectionDetails.validity_months);
      // Use parseISO to avoid timezone issues when parsing date strings
      // new Date('YYYY-MM-DD') interprets the date as UTC midnight, which can shift
      // to the previous day in timezones ahead of UTC. parseISO treats it as local date.
      const inspDate = parseISO(inspectionDetails.inspection_date);
      const expiryDate = addMonths(inspDate, months);
      return format(expiryDate, 'yyyy-MM-dd');
    }
    return '';
  }, [inspectionDetails.inspection_date, inspectionDetails.validity_months]);

  // Calculate cylinder expiry dates automatically
  useEffect(() => {
    setCylinders(prev => prev.map(cyl => {
      if (cyl.manufacturing_date && cyl.life_span) {
        const expiry = addYears(parseISO(cyl.manufacturing_date), parseInt(cyl.life_span));
        return { ...cyl, expiry_date: format(expiry, 'yyyy-MM-dd') };
      }
      return cyl;
    }));
  }, [cylinders.map(c => c.manufacturing_date + c.life_span).join(',')]);

  useEffect(() => {
    loadConfig();
    loadWorkshops();
  }, []);

  // Load existing draft when ?/inspections/:id/edit is opened
  useEffect(() => {
    if (!draftId) return;
    (async () => {
      try {
        const [inspRes, cylRes] = await Promise.all([
          inspectionsAPI.getById(draftId),
          inspectionsAPI.getCylinders(draftId),
        ]);
        const i = inspRes.data || {};
        const cyls = cylRes.data || [];

        // Facility type comes from the inspection's associated record
        const rt = i.record_type || facilityTypeParam || 'car';
        setFacilityType(rt);
        const _isTrailer = rt === 'trailer';
        const _isStorage = rt === 'cng_storage';
        const _isSpecial = _isTrailer || _isStorage;

        // Step 1 — Inspection details
        setInspectionDetails({
          inspection_type: ({ new: 'new_installation', periodic: 'periodic', after_accident: 'after_accident' })[i.inspection_type] || i.inspection_type || 'new_installation',
          validity_months: i.validity_months || 12,
          inspection_date: i.inspection_date ? format(parseISO(i.inspection_date), 'yyyy-MM-dd') : (i.valid_from ? format(parseISO(i.valid_from), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd')),
          ngv_origin: i.ngv_origin || 'oem',
          installer_id: i.installer_id || '',
          installer_name: i.installer_name || '',
        });

        // Step 2 — Vehicle & owner (reverse the mapping used in buildPayload)
        setVehicleOwnerInfo({
          owner_name: i.owner_name || '',
          owner_phone: i.owner_phone || '',
          owner_email: i.owner_email || '',
          registration_number: _isStorage ? (i.storage_serial_number || i.serial_number || '') : (i.registration_number || ''),
          vehicle_type: rt,
          fuel_type: _isSpecial ? (i.product_name || '') : (i.fuel_type || 'bi_fuel'),
          make: _isSpecial ? (i.manufacturer || '') : (i.make || ''),
          model: _isTrailer ? (i.product_serial || '') : (_isStorage ? (i.facility_category || '') : (i.model || '')),
          year: i.year || '',
          chassis_number: _isStorage ? (i.storage_installation_position || '') : (i.chassis_number || ''),
          engine_number: _isTrailer ? (i.container_number || '') : (_isStorage ? (i.user_name || '') : (i.engine_number || '')),
          mileage: _isSpecial ? (i.capacity || '') : (i.mileage || ''),
          region: i.region || '',
        });

        // Step 3 — Cylinders
        if (cyls.length > 0) {
          setCylinders(cyls.map(c => ({
            serial_number: c.serial_number || '',
            cylinder_type: c.cylinder_type || 'I',
            manufacturer: c.manufacturer || '',
            installation_position: c.installation_position || 'boot_trunk',
            service_pressure: c.service_pressure != null ? String(c.service_pressure) : '20',
            water_volume: c.water_volume != null ? String(c.water_volume) : '',
            testing_pressure: c.testing_pressure != null ? String(c.testing_pressure) : '30',
            manufacturing_date: c.manufacturing_date ? String(c.manufacturing_date).slice(0, 10) : '',
            life_span: c.life_span != null ? String(c.life_span) : '15',
            expiry_date: c.expiry_date ? String(c.expiry_date).slice(0, 10) : '',
            isDuplicate: false,
          })));
        }

        // Step 4 — Results
        const toUi = (b) => (b === true ? 'pass' : b === false ? 'fail' : null);
        setInspectionResults({
          visual_inspection: toUi(i.visual_inspection_passed),
          gas_leak_test: toUi(i.gas_leak_test_passed ?? i.leak_test_passed),
          pressure_test: toUi(i.pressure_test_passed),
          ndt_test: toUi(i.ndt_test_passed),
          overall_result: i.overall_result || 'approved',
          notes: i.notes || '',
        });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load draft');
        navigate('/inspections');
      } finally {
        setIsLoadingDraft(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  // Debounced real-time duplicate check for registration / storage serial number
  useEffect(() => {
    const value = (vehicleOwnerInfo.registration_number || '').trim();
    if (!value || value.length < 3) {
      setRegDuplicate(null);
      setRegChecking(false);
      return undefined;
    }
    setRegChecking(true);
    const t = setTimeout(async () => {
      try {
        const api = isStorage
          ? validationAPI.checkStorageSerial(value, recordId || undefined)
          : validationAPI.checkRegistration(value, recordId || undefined);
        const res = await api;
        if (res.data?.is_duplicate) {
          setRegDuplicate({
            field: res.data.field || (isStorage ? 'storage_serial_number' : 'registration_number'),
            value: res.data.value || value,
            existing_record: res.data.existing_record,
          });
        } else {
          setRegDuplicate(null);
        }
      } catch (err) {
        // Silent on background check; toast only on submit
        console.error('Duplicate check failed:', err);
        setRegDuplicate(null);
      } finally {
        setRegChecking(false);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [vehicleOwnerInfo.registration_number, isStorage, recordId]);

  // Real-time per-cylinder duplicate detection: in-form (against other rows) + DB (against all cylinders).
  useEffect(() => {
    const rowMap = {};

    // 1) In-form duplicate check (case-insensitive, ignores empty)
    const seenIndexByKey = {};
    cylinders.forEach((cyl, idx) => {
      const key = (cyl.serial_number || '').replace(/\s/g, '').toUpperCase();
      if (!key) return;
      if (seenIndexByKey[key] !== undefined) {
        // Both rows are duplicates of each other
        const firstIdx = seenIndexByKey[key];
        rowMap[idx] = { value: key, inFormLocation: `Cylinder #${firstIdx + 1}` };
        if (!rowMap[firstIdx]) {
          rowMap[firstIdx] = { value: key, inFormLocation: `Cylinder #${idx + 1}` };
        }
      } else {
        seenIndexByKey[key] = idx;
      }
    });

    // 2) DB duplicate check (debounced)
    const serials = cylinders.map(c => (c.serial_number || '').replace(/\s/g, '').toUpperCase()).filter(Boolean);
    if (serials.length === 0) {
      setCylinderDupByRow(rowMap);
      setCylinderDuplicates([]);
      return undefined;
    }

    setCylinderChecking(true);
    const t = setTimeout(async () => {
      try {
        const excludeCylinderIds = cylinders.map(c => c.id).filter(Boolean);
        const res = await validationAPI.checkCylindersBatch(serials, recordId || null, excludeCylinderIds);
        const dups = res.data?.duplicates || [];
        const dupBySerial = {};
        dups.forEach(d => { dupBySerial[d.serial_number] = d; });

        // Merge DB duplicates into rowMap (without overwriting in-form info)
        cylinders.forEach((cyl, idx) => {
          const key = (cyl.serial_number || '').replace(/\s/g, '').toUpperCase();
          if (!key) return;
          const dbHit = dupBySerial[key];
          if (!dbHit) return;
          rowMap[idx] = {
            ...(rowMap[idx] || {}),
            value: key,
            existing_record: dbHit.existing_record,
            same_facility: dbHit.same_facility,
          };
        });

        setCylinderDupByRow(rowMap);
        setCylinderDuplicates(dups);
      } catch (err) {
        console.error('Cylinder duplicate check failed:', err);
        setCylinderDupByRow(rowMap);
      } finally {
        setCylinderChecking(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [cylinders, recordId]);

  const loadConfig = async () => {
    try {
      const configRes = await adminAPI.getConfig();
      setConfig(configRes.data);
    } catch (err) {
      console.error('Failed to load config:', err);
    }
  };

  const loadWorkshops = async () => {
    try {
      const res = await workshopsAPI.getAll({ status: 'active' });
      setWorkshops(res.data || []);
    } catch (err) {
      console.error('Failed to load workshops:', err);
    }
  };

  // Cylinder management
  const handleAddCylinder = () => {
    if (cylinders.length >= config.max_cylinders) {
      toast.error(`Maximum ${config.max_cylinders} cylinders allowed`);
      return;
    }
    setCylinders([...cylinders, {
      serial_number: '',
      cylinder_type: 'I',
      manufacturer: '',
      installation_position: 'boot_trunk',
      service_pressure: '20',
      water_volume: '',
      testing_pressure: '30',
      manufacturing_date: '',
      life_span: '15',
      expiry_date: '',
      isDuplicate: false, // Track if this is a duplicated cylinder
    }]);
  };

  // Add similar cylinder - duplicates all fields except serial number
  const handleAddSimilarCylinder = (sourceIndex) => {
    if (cylinders.length >= config.max_cylinders) {
      toast.error(`Maximum ${config.max_cylinders} cylinders allowed`);
      return;
    }
    
    const sourceCylinder = cylinders[sourceIndex];
    const newCylinder = {
      ...sourceCylinder,
      serial_number: '', // Clear serial number - must be unique
      isDuplicate: true, // Mark as duplicated for UI purposes
    };
    
    setCylinders([...cylinders, newCylinder]);
    toast.success('Cylinder duplicated. Please enter a unique serial number.');
  };

  // Bulk add multiple similar cylinders at once
  const handleBulkAddSimilarCylinders = (sourceIndex, count) => {
    const remaining = config.max_cylinders - cylinders.length;
    const toAdd = Math.min(count, remaining);
    
    if (toAdd <= 0) {
      toast.error(`Maximum ${config.max_cylinders} cylinders allowed`);
      return;
    }
    
    const sourceCylinder = cylinders[sourceIndex];
    const newCylinders = [];
    
    for (let i = 0; i < toAdd; i++) {
      newCylinders.push({
        ...sourceCylinder,
        serial_number: '', // Clear serial number - must be unique
        isDuplicate: true,
      });
    }
    
    setCylinders([...cylinders, ...newCylinders]);
    toast.success(`Added ${toAdd} cylinder(s). Please enter unique serial numbers for each.`);
  };

  // File upload handler for test reports
  const handleFileUpload = async (testType, file) => {
    if (!file) return;
    
    // Validate file type (PDF or Image)
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only PDF and image files (JPEG, PNG, WebP) are allowed');
      return;
    }
    
    // Max file size: 10MB
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setUploading(prev => ({ ...prev, [testType]: true }));
    
    try {
      // Convert to base64 for storage
      const reader = new FileReader();
      reader.onload = () => {
        setTestReportFiles(prev => ({
          ...prev,
          [testType]: {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result,
          }
        }));
        setUploading(prev => ({ ...prev, [testType]: false }));
        toast.success(`${testType.replace(/_/g, ' ')} uploaded successfully`);
      };
      reader.onerror = () => {
        setUploading(prev => ({ ...prev, [testType]: false }));
        toast.error('Failed to read file');
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setUploading(prev => ({ ...prev, [testType]: false }));
      toast.error('Failed to upload file');
    }
  };

  const handleRemoveFile = (testType) => {
    setTestReportFiles(prev => ({ ...prev, [testType]: null }));
  };

  const handleRemoveCylinder = (index) => {
    if (cylinders.length === 1) {
      toast.error('At least one cylinder is required');
      return;
    }
    setCylinders(cylinders.filter((_, i) => i !== index));
  };

  const updateCylinder = (index, field, value) => {
    const updated = [...cylinders];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-calculate testing_pressure based on service_pressure
    // 20 MPa → 30 MPa (1.5x)
    // 25 MPa → 37.5 MPa (1.5x)
    if (field === 'service_pressure') {
      const testingPressure = value === '20' ? '30' : value === '25' ? '37.5' : '';
      updated[index].testing_pressure = testingPressure;
    }
    
    // Clear cross-record duplicate alert for the previous serial value when user edits it
    if (field === 'serial_number') {
      const oldSerial = (cylinders[index]?.serial_number || '').replace(/\s/g, '').toUpperCase();
      if (oldSerial) {
        setCylinderDuplicates(prev => prev.filter(d => d.serial_number !== oldSerial));
      }
    }
    
    setCylinders(updated);
  };

  // Step validation
  const validateStep = async (stepNum) => {
    switch (stepNum) {
      case 1:
        if (!inspectionDetails.inspection_type) {
          toast.error('Please select an inspection type');
          return false;
        }
        if (!inspectionDetails.inspection_date) {
          toast.error('Please select an inspection date');
          return false;
        }
        return true;
      case 2:
        if (!vehicleOwnerInfo.owner_name) {
          toast.error('Owner name is required');
          return false;
        }
        if (!vehicleOwnerInfo.owner_phone) {
          toast.error('Owner phone number is required');
          return false;
        }
        // Validate phone number format (10 digits)
        const phoneClean = vehicleOwnerInfo.owner_phone.replace(/\D/g, '');
        if (phoneClean.length !== 10) {
          toast.error('Phone number must be exactly 10 digits (e.g., 0742222812)');
          return false;
        }
        
        // For CNG Storage - validate serial number
        if (isStorage) {
          if (!vehicleOwnerInfo.registration_number) {
            toast.error('Storage Serial Number is required');
            return false;
          }
          // If real-time check already flagged a duplicate, block immediately
          if (regDuplicate) {
            toast.error(`Storage Serial Number "${regDuplicate.value}" already exists. Please use a different number or open the existing record.`, { duration: 6000 });
            return false;
          }
          // Final duplicate check just before progressing
          try {
            setValidating(true);
            const res = await validationAPI.checkStorageSerial(vehicleOwnerInfo.registration_number, recordId || undefined);
            if (res.data.is_duplicate) {
              setRegDuplicate({
                field: 'storage_serial_number',
                value: res.data.value || vehicleOwnerInfo.registration_number,
                existing_record: res.data.existing_record,
              });
              toast.error(`Storage Serial Number "${vehicleOwnerInfo.registration_number}" already exists. Please review the existing record below.`, { duration: 6000 });
              return false;
            }
          } catch (err) {
            console.error('Storage serial validation failed:', err);
            toast.error('Failed to validate storage serial number. Please try again.');
            return false;
          } finally {
            setValidating(false);
          }
        } else {
          // For Vehicles - validate registration number
          if (!vehicleOwnerInfo.registration_number) {
            toast.error('Vehicle registration number is required');
            return false;
          }
          // Check for duplicate registration number (skip if editing existing record)
          if (!recordId) {
            if (regDuplicate) {
              toast.error(`Registration Number "${regDuplicate.value}" already exists. Please use a different number or open the existing record.`, { duration: 6000 });
              return false;
            }
            try {
              setValidating(true);
              const res = await validationAPI.checkRegistration(vehicleOwnerInfo.registration_number, recordId || undefined);
              if (res.data.is_duplicate) {
                setRegDuplicate({
                  field: 'registration_number',
                  value: res.data.value || vehicleOwnerInfo.registration_number,
                  existing_record: res.data.existing_record,
                });
                toast.error(`Registration Number "${vehicleOwnerInfo.registration_number}" already exists. Please review the existing record below.`, { duration: 6000 });
                return false;
              }
            } catch (err) {
              console.error('Registration validation failed:', err);
              toast.error('Failed to validate registration number. Please try again.');
              return false;
            } finally {
              setValidating(false);
            }
          }
        }
        
        if (!vehicleOwnerInfo.region) {
          toast.error('Please select a region');
          return false;
        }
        return true;
      case 3:
        // Check for expired cylinders first
        const inspectionDate = inspectionDetails.inspection_date ? parseISO(inspectionDetails.inspection_date) : new Date();
        const expiredCyls = [];
        
        for (let i = 0; i < cylinders.length; i++) {
          if (!cylinders[i].serial_number) {
            toast.error(`Cylinder ${i + 1}: Serial number is required`);
            return false;
          }
          if (!cylinders[i].manufacturing_date) {
            toast.error(`Cylinder ${i + 1}: Manufacturing date is required`);
            return false;
          }
          // Service Pressure is mandatory for all cylinder types
          if (!cylinders[i].service_pressure) {
            toast.error(`Cylinder ${i + 1}: Service Pressure is required`);
            return false;
          }
          
          // Check if cylinder has expired before or on inspection date
          if (cylinders[i].expiry_date) {
            const expiryDate = parseISO(cylinders[i].expiry_date);
            if (expiryDate < inspectionDate) {
              expiredCyls.push({
                index: i,
                serial: cylinders[i].serial_number || `Cylinder ${i + 1}`,
                expiry_date: cylinders[i].expiry_date
              });
            }
          }
        }
        
        // If any cylinders are expired, block submission
        if (expiredCyls.length > 0) {
          setExpiredCylinders(expiredCyls.map(c => c.index));
          const expiredList = expiredCyls.map(c => 
            `${c.serial} (expires ${format(parseISO(c.expiry_date), 'dd MMM yyyy')})`
          ).join(', ');
          toast.error(
            `Cannot proceed: ${expiredCyls.length} cylinder(s) have expired before the inspection date: ${expiredList}. Expired cylinders must be replaced before inspection.`,
            { duration: 10000 }
          );
          return false;
        } else {
          setExpiredCylinders([]); // Clear expired state if all cylinders are valid
        }
        
        // Check for duplicate serial numbers within the form
        const serials = cylinders.map(c => c.serial_number).filter(Boolean);
        const dupRows = Object.keys(cylinderDupByRow);
        if (dupRows.length > 0) {
          // Highlight the relevant cylinders by setting expired-style ring? We already render per-row alerts.
          toast.error(
            `${dupRows.length} cylinder serial number(s) are duplicates. Resolve the highlighted issues to proceed.`,
            { duration: 6000 }
          );
          return false;
        }
        if (serials.length !== new Set(serials.map(s => s.replace(/\s/g, '').toUpperCase())).size) {
          toast.error('Cylinder serial numbers must be unique within this inspection');
          return false;
        }

        // Final guard: re-run DB batch check (in case user typed faster than debounce)
        if (serials.length > 0) {
          try {
            setValidating(true);
            const excludeCylinderIds = cylinders.map(c => c.id).filter(Boolean);
            const res = await validationAPI.checkCylindersBatch(serials, recordId || null, excludeCylinderIds);
            if (res.data.has_duplicates) {
              const duplicates = res.data.duplicates || [];
              setCylinderDuplicates(duplicates);
              const dupList = duplicates.map(d => {
                const regNum = d.existing_record?.registration_number || d.existing_record?.storage_serial_number || 'Unknown';
                const where = d.same_facility ? `same facility — ${regNum}` : regNum;
                return `"${d.serial_number}" (in ${where})`;
              }).join(', ');
              toast.error(
                `Duplicate cylinder serial number(s) found: ${dupList}. Review the existing records highlighted below or use different serial numbers.`,
                { duration: 8000 }
              );
              return false;
            }
            setCylinderDuplicates([]);
          } catch (err) {
            console.error('Cylinder validation failed:', err);
            toast.error('Failed to validate cylinder serial numbers. Please try again.');
            return false;
          } finally {
            setValidating(false);
          }
        }
        return true;
      case 4:
        if (!inspectionResults.visual_inspection) {
          toast.error('Please select a result for Visual Inspection (Pass, Fail, or Not Conducted)');
          return false;
        }
        if (!inspectionResults.gas_leak_test) {
          toast.error('Please select a result for Gas Leak Test (Pass, Fail, or Not Conducted)');
          return false;
        }
        if (!inspectionResults.overall_result) {
          toast.error('Overall result is required');
          return false;
        }
        // Trailer and Storage specific validation (both require same tests)
        if (isSpecialType) {
          const typeLabel = isTrailer ? 'trailer' : 'CNG storage';
          // NDT test selection is required for trailers and storage
          if (!inspectionResults.ndt_test) {
            toast.error(`Please select a result for NDT test (Pass, Fail, or Not Conducted) — required for ${typeLabel} inspections`);
            return false;
          }
          // Leak test report is mandatory only if the test was actually conducted
          if (inspectionResults.gas_leak_test !== 'not_conducted' && !testReportFiles.leak_test_report) {
            toast.error(`Leak test report/certificate upload is required for ${typeLabel} when the test is conducted`);
            return false;
          }
          // NDT test report is mandatory only if the test was actually conducted
          if (inspectionResults.ndt_test !== 'not_conducted' && !testReportFiles.ndt_test_report) {
            toast.error(`NDT test report/certificate upload is required for ${typeLabel} when the test is conducted`);
            return false;
          }
          // Pressure test report is mandatory if pressure test was conducted (pass/fail)
          if (inspectionResults.pressure_test && inspectionResults.pressure_test !== 'not_conducted' && !testReportFiles.pressure_test_report) {
            toast.error('Pressure test report/certificate upload is required when pressure test is conducted');
            return false;
          }
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = async () => {
    const isValid = await validateStep(step);
    if (isValid) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    setStep(Math.max(1, step - 1));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const payload = buildPayload(true);
      if (draftId) {
        await inspectionsAPI.updateDraft(draftId, payload);
        toast.success('Draft updated');
      } else {
        const res = await inspectionsAPI.create(payload);
        toast.success('Draft saved successfully');
        if (res.data?.id) navigate(`/inspections/${res.data.id}/edit`, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const buildPayload = (isDraft = false) => {
    // Build trailer-specific field mappings
    const trailerFields = isTrailer ? {
      product_name: vehicleOwnerInfo.fuel_type, // fuel_type -> product_name
      manufacturer: vehicleOwnerInfo.make, // make -> manufacturer
      product_serial: vehicleOwnerInfo.model, // model -> product_serial
      container_number: vehicleOwnerInfo.engine_number, // engine_number -> container_number
      capacity: vehicleOwnerInfo.mileage ? parseFloat(vehicleOwnerInfo.mileage) : null, // mileage -> capacity (m³)
    } : {};

    // Build storage-specific field mappings
    const storageFields = isStorage ? {
      serial_number: vehicleOwnerInfo.registration_number.replace(/\s/g, '').toUpperCase(), // registration_number -> serial_number
      product_name: vehicleOwnerInfo.fuel_type, // fuel_type -> product_name
      manufacturer: vehicleOwnerInfo.make, // make -> manufacturer
      facility_category: vehicleOwnerInfo.model, // model -> facility_category
      storage_installation_position: vehicleOwnerInfo.chassis_number, // chassis_number -> installation_position
      user_name: vehicleOwnerInfo.engine_number, // engine_number -> user_name
      capacity: vehicleOwnerInfo.mileage ? parseFloat(vehicleOwnerInfo.mileage) : null, // mileage -> capacity (m³)
    } : {};

    return {
      // Facility type (locked)
      record_type: facilityType,
      record_id: recordId || null,
      
      // Step 1: Inspection Details
      inspection_type: inspectionDetails.inspection_type,
      validity_months: parseInt(inspectionDetails.validity_months),
      inspection_date: inspectionDetails.inspection_date,
      valid_from: inspectionDetails.inspection_date,
      valid_to: validTo,
      // NGV origin not applicable for trailers/storage
      ngv_origin: isSpecialType ? null : inspectionDetails.ngv_origin,
      // Installer info - only for retrofitted vehicles
      installer_id: (!isSpecialType && inspectionDetails.ngv_origin === 'retrofitted' && inspectionDetails.installer_id !== 'other') ? inspectionDetails.installer_id : null,
      installer_name: (!isSpecialType && inspectionDetails.ngv_origin === 'retrofitted') ? inspectionDetails.installer_name : null,
      
      // Step 2: Vehicle & Owner Info
      owner_name: vehicleOwnerInfo.owner_name,
      owner_phone: vehicleOwnerInfo.owner_phone.replace(/\D/g, ''), // Clean phone number
      owner_email: vehicleOwnerInfo.owner_email || null,
      // For storage, use storage_serial_number field instead of registration_number
      registration_number: isStorage ? null : vehicleOwnerInfo.registration_number.replace(/\s/g, '').toUpperCase(),
      storage_serial_number: isStorage ? vehicleOwnerInfo.registration_number.replace(/\s/g, '').toUpperCase() : null,
      // Standard vehicle fields (mapped differently for trailer/storage)
      fuel_type: isSpecialType ? null : vehicleOwnerInfo.fuel_type,
      make: isSpecialType ? null : vehicleOwnerInfo.make,
      model: isSpecialType ? null : vehicleOwnerInfo.model,
      year: vehicleOwnerInfo.year ? parseInt(vehicleOwnerInfo.year) : null,
      chassis_number: isStorage ? null : vehicleOwnerInfo.chassis_number,
      engine_number: isSpecialType ? null : vehicleOwnerInfo.engine_number,
      mileage: isSpecialType ? null : (vehicleOwnerInfo.mileage ? parseInt(vehicleOwnerInfo.mileage) : null),
      region: vehicleOwnerInfo.region,
      // Special type fields
      ...trailerFields,
      ...storageFields,
      
      // Step 3: Cylinders
      cylinders: cylinders.map(cyl => ({
        serial_number: cyl.serial_number,
        cylinder_type: cyl.cylinder_type,
        manufacturer: cyl.manufacturer,
        // Installation position not applicable for trailers/storage
        installation_position: isSpecialType ? null : cyl.installation_position,
        service_pressure: cyl.service_pressure ? parseFloat(cyl.service_pressure) : null,
        // For trailers/storage: use testing_pressure instead of water_volume
        water_volume: isSpecialType ? null : (cyl.water_volume ? parseFloat(cyl.water_volume) : null),
        testing_pressure: isSpecialType ? parseFloat(cyl.testing_pressure) : null,
        manufacturing_date: cyl.manufacturing_date,
        life_span: cyl.life_span ? parseInt(cyl.life_span) : null,
        expiry_date: cyl.expiry_date,
      })),
      
      // Step 4: Inspection Results
      // Map 3-state UI values to backend bool/null: 'pass' -> true, 'fail' -> false, 'not_conducted' / null -> null
      visual_inspection_passed: inspectionResults.visual_inspection === 'pass' ? true : inspectionResults.visual_inspection === 'fail' ? false : null,
      gas_leak_test_passed: inspectionResults.gas_leak_test === 'pass' ? true : inspectionResults.gas_leak_test === 'fail' ? false : null,
      pressure_test_passed: inspectionResults.pressure_test === 'pass' ? true : inspectionResults.pressure_test === 'fail' ? false : null,
      ndt_test_passed: inspectionResults.ndt_test === 'pass' ? true : inspectionResults.ndt_test === 'fail' ? false : null,
      overall_result: inspectionResults.overall_result,
      notes: inspectionResults.notes,
      
      // Test report attachments (for trailers and storage)
      test_report_attachments: isSpecialType ? {
        leak_test_report: testReportFiles.leak_test_report,
        ndt_test_report: testReportFiles.ndt_test_report,
        pressure_test_report: testReportFiles.pressure_test_report,
      } : null,
      
      // Draft flag
      is_draft: isDraft,
    };
  };

  const handleSubmit = async () => {
    // Validate all steps
    for (let i = 1; i <= 4; i++) {
      // validateStep is async for some steps (duplicate checks). Await it.
      if (!(await validateStep(i))) {
        setStep(i);
        return;
      }
    }

    setLoading(true);
    try {
      const payload = buildPayload(false);
      let response;
      if (draftId) {
        response = await inspectionsAPI.updateDraft(draftId, payload);
      } else {
        response = await inspectionsAPI.create(payload);
      }
      toast.success('Inspection submitted successfully!');

      // Navigate to certificate actions page
      const newId = response.data?.id || response.data?.inspection_id || draftId;
      if (newId) {
        navigate(`/inspections/${newId}/certificate-actions`);
      } else {
        navigate('/inspections');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit inspection');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, label: 'Inspection Details', icon: ClipboardCheck },
    { num: 2, label: isStorage ? 'Storage & Owner' : (isTrailer ? 'Trailer & Owner' : 'Vehicle & Owner'), icon: isStorage ? Package : (isTrailer ? Truck : Car) },
    { num: 3, label: 'CNG Cylinders', icon: Cylinder },
    { num: 4, label: 'Results', icon: CheckCircle },
    { num: 5, label: 'Confirm', icon: Shield },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn pb-8" data-testid="inspection-form">
      {/* Header with Locked Facility Type */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{getRecordTypeEmoji(facilityType)}</span>
            <div>
              <h1 className="font-heading text-2xl font-bold">
                New {getRecordTypeLabel(facilityType)} Inspection
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <Lock className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  Facility type locked for this inspection
                </span>
              </div>
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={handleSaveDraft} disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save Draft
        </Button>
      </div>

      {/* Progress Steps */}
      <div className="bg-card rounded-lg p-4 border">
        <div className="flex justify-between items-center">
          {steps.map((s, idx) => {
            const Icon = s.icon;
            const isActive = step === s.num;
            const isCompleted = step > s.num;
            return (
              <React.Fragment key={s.num}>
                <button
                  onClick={() => s.num < step && setStep(s.num)}
                  disabled={s.num > step}
                  className={`flex flex-col items-center gap-1 transition-all ${
                    isActive ? 'text-primary' : 
                    isCompleted ? 'text-emerald-600' : 
                    'text-muted-foreground'
                  } ${s.num < step ? 'cursor-pointer hover:text-primary' : ''}`}
                >
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center transition-all ${
                    isActive ? 'bg-primary text-primary-foreground' :
                    isCompleted ? 'bg-emerald-100 text-emerald-600' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {isCompleted ? <CheckCircle className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </div>
                  <span className="text-xs font-medium hidden md:block">{s.label}</span>
                </button>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    step > s.num ? 'bg-emerald-500' : 'bg-muted'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <Progress value={(step / 5) * 100} className="h-1 mt-4" />
      </div>

      {/* Step 1: Inspection Details */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-primary" />
              Step 1: Inspection Details
            </CardTitle>
            <CardDescription>
              Basic information about this inspection
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Inspection Type */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Inspection Type *</Label>
              <RadioGroup
                value={inspectionDetails.inspection_type}
                onValueChange={(v) => {
                  // For periodic or after_accident, redirect to recertification page
                  if (v === 'periodic' || v === 'after_accident') {
                    navigate(`/inspections/recertify?type=${v}`);
                    return;
                  }
                  setInspectionDetails({ ...inspectionDetails, inspection_type: v });
                }}
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                {INSPECTION_TYPES.map((type) => (
                  <div key={type.value}>
                    <RadioGroupItem
                      value={type.value}
                      id={type.value}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={type.value}
                      className={cn(
                        "flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all",
                        (type.value === 'periodic' || type.value === 'after_accident') && "border-dashed"
                      )}
                    >
                      <span>{type.label}</span>
                      {(type.value === 'periodic' || type.value === 'after_accident') && (
                        <span className="text-xs text-muted-foreground mt-1">(Search existing record)</span>
                      )}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Validity Period and Inspection Date */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-base font-semibold">Validity Period *</Label>
                <Select
                  value={String(inspectionDetails.validity_months)}
                  onValueChange={(v) => setInspectionDetails({ ...inspectionDetails, validity_months: Number(v) })}
                >
                  <SelectTrigger data-testid="validity-period">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VALIDITY_PERIODS.map((period) => (
                      <SelectItem key={period.value} value={period.value}>
                        {period.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-base font-semibold">Inspection Date *</Label>
                <Input
                  type="date"
                  value={inspectionDetails.inspection_date}
                  onChange={(e) => setInspectionDetails({ ...inspectionDetails, inspection_date: e.target.value })}
                  data-testid="inspection-date"
                />
              </div>
            </div>

            {/* Auto-calculated Valid To - READ-ONLY */}
            <div className="p-4 bg-muted/50 rounded-lg border-2 border-dashed border-muted">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Valid To (Auto-calculated - Read Only)</p>
                  <p className="text-lg font-semibold text-primary">
                    {validTo ? format(parseISO(validTo), 'dd/MM/yyyy') : '-'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {validTo ? format(parseISO(validTo), 'dd MMMM yyyy') : ''}
                  </p>
                </div>
                <div className="flex flex-col items-center">
                  <Calendar className="h-8 w-8 text-primary" />
                  <span className="text-xs text-muted-foreground mt-1">Auto</span>
                </div>
              </div>
            </div>

            {/* NGV System Origin - NOT shown for Trailers or Storage */}
            {!isSpecialType && (
              <>
                <Separator />
                <div className="space-y-3">
                  <Label className="text-base font-semibold">NGV System Origin *</Label>
                  <RadioGroup
                    value={inspectionDetails.ngv_origin}
                    onValueChange={(v) => setInspectionDetails({ ...inspectionDetails, ngv_origin: v, installer_name: v === 'oem' ? '' : inspectionDetails.installer_name })}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  >
                    {NGV_ORIGINS.map((origin) => (
                      <div key={origin.value}>
                        <RadioGroupItem
                          value={origin.value}
                          id={`ngv-${origin.value}`}
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor={`ngv-${origin.value}`}
                          className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all"
                        >
                          <span className="font-semibold">{origin.label}</span>
                          <span className="text-xs text-muted-foreground mt-1">{origin.description}</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {/* Installer/Workshop Selection - Only shown if Retrofitted */}
                {inspectionDetails.ngv_origin === 'retrofitted' && (
                  <div className="space-y-4 animate-fadeIn">
                    <Label className="text-base font-semibold">Name of Installer *</Label>
                    <Select
                      value={inspectionDetails.installer_id}
                      onValueChange={(v) => {
                        if (v === 'other') {
                          setInspectionDetails({ ...inspectionDetails, installer_id: 'other', installer_name: '' });
                        } else {
                          const workshop = workshops.find(w => w.id === v);
                          setInspectionDetails({ 
                            ...inspectionDetails, 
                            installer_id: v, 
                            installer_name: workshop?.name || ''
                          });
                        }
                      }}
                    >
                      <SelectTrigger data-testid="installer-select">
                        <SelectValue placeholder="Select conversion workshop" />
                      </SelectTrigger>
                      <SelectContent>
                        {workshops.map((workshop) => (
                          <SelectItem key={workshop.id} value={workshop.id}>
                            {workshop.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="other">Other (Manual Entry)</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Manual entry field when "Other" is selected */}
                    {inspectionDetails.installer_id === 'other' && (
                      <div className="space-y-2 pt-2">
                        <Label>Enter Installer Name</Label>
                        <Input
                          value={inspectionDetails.installer_name}
                          onChange={(e) => setInspectionDetails({ ...inspectionDetails, installer_name: e.target.value })}
                          placeholder="Enter installer/workshop name"
                          data-testid="installer-name-manual"
                        />
                      </div>
                    )}
                    
                    <p className="text-xs text-muted-foreground">
                      Select the registered workshop or enter manually if not listed
                    </p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Vehicle & Owner Information (or Trailer/Storage Information) */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {isStorage ? <Package className="h-5 w-5 text-primary" /> : (isTrailer ? <Truck className="h-5 w-5 text-primary" /> : <Car className="h-5 w-5 text-primary" />)}
              Step 2: {isStorage ? 'Storage & Owner Information' : (isTrailer ? 'Trailer & Owner Information' : 'Vehicle & Owner Information')}
            </CardTitle>
            <CardDescription>
              Enter {isStorage ? 'storage facility' : (isTrailer ? 'trailer' : 'vehicle')} and owner details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Owner Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Owner Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Owner Name *</Label>
                  <Input
                    value={vehicleOwnerInfo.owner_name}
                    onChange={(e) => setVehicleOwnerInfo({ ...vehicleOwnerInfo, owner_name: e.target.value })}
                    placeholder="Enter owner's full name"
                    data-testid="owner-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Phone Number *</Label>
                  <Input
                    value={vehicleOwnerInfo.owner_phone}
                    onChange={(e) => {
                      // Only allow digits, max 10
                      const cleaned = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setVehicleOwnerInfo({ ...vehicleOwnerInfo, owner_phone: cleaned });
                    }}
                    placeholder="0742222812"
                    className="font-mono"
                    maxLength={10}
                    data-testid="owner-phone"
                  />
                  <p className="text-xs text-muted-foreground">10 digits required (e.g., 0742222812)</p>
                </div>

                <div className="space-y-2">
                  <Label>Email (Optional)</Label>
                  <Input
                    type="email"
                    value={vehicleOwnerInfo.owner_email}
                    onChange={(e) => setVehicleOwnerInfo({ ...vehicleOwnerInfo, owner_email: e.target.value })}
                    placeholder="owner@example.com"
                    data-testid="owner-email"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Vehicle/Trailer/Storage Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                {isStorage ? 'Storage Information' : (isTrailer ? 'Trailer Information' : 'Vehicle Information')}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Registration Number (Vehicle/Trailer) / Serial Number (Storage) */}
                <div className="space-y-2 md:col-span-2">
                  <Label>{isStorage ? 'Serial Number *' : 'Registration Number *'}</Label>
                  <div className="relative">
                    <Input
                      value={vehicleOwnerInfo.registration_number}
                      onChange={(e) => {
                        // Remove spaces and convert to uppercase
                        const cleaned = e.target.value.replace(/\s/g, '').toUpperCase();
                        setVehicleOwnerInfo({ ...vehicleOwnerInfo, registration_number: cleaned });
                      }}
                      placeholder={isStorage ? 'SN-2024-001' : 'T481ELN'}
                      className={`font-mono uppercase pr-10 ${regDuplicate ? 'border-amber-500 ring-2 ring-amber-200 focus-visible:ring-amber-300' : ''}`}
                      data-testid="registration-number"
                      aria-invalid={!!regDuplicate}
                    />
                    {regChecking && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{isStorage ? 'Enter storage serial number' : 'No spaces (e.g., T481ELN)'}</p>
                  {regDuplicate && (
                    <DuplicateAlert
                      field={regDuplicate.field}
                      value={regDuplicate.value}
                      existingRecord={regDuplicate.existing_record}
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{isStorage ? 'Equipment Type' : (isTrailer ? 'Asset Type' : 'Vehicle Type')}</Label>
                  <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-muted/50">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{getRecordTypeEmoji(facilityType)} {getRecordTypeLabel(facilityType)}</span>
                    <Badge variant="outline" className="ml-auto text-xs">Locked</Badge>
                  </div>
                </div>

                {/* Fuel Type (Vehicle) / Product Name (Trailer/Storage) */}
                <div className="space-y-2">
                  <Label>{isSpecialType ? 'Product Name *' : 'Fuel Type *'}</Label>
                  {isSpecialType ? (
                    <Input
                      value={vehicleOwnerInfo.fuel_type}
                      onChange={(e) => setVehicleOwnerInfo({ ...vehicleOwnerInfo, fuel_type: e.target.value })}
                      placeholder="Enter product name"
                      data-testid="product-name"
                    />
                  ) : (
                    <Select
                      value={vehicleOwnerInfo.fuel_type}
                      onValueChange={(v) => setVehicleOwnerInfo({ ...vehicleOwnerInfo, fuel_type: v })}
                    >
                      <SelectTrigger data-testid="fuel-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FUEL_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Make (Vehicle) / Manufacturer (Trailer/Storage) */}
                <div className="space-y-2">
                  <Label>{isSpecialType ? 'Manufacturer' : 'Make'}</Label>
                  <Input
                    value={vehicleOwnerInfo.make}
                    onChange={(e) => setVehicleOwnerInfo({ ...vehicleOwnerInfo, make: e.target.value })}
                    placeholder={isSpecialType ? 'Enter manufacturer' : 'Toyota, HOWO, etc.'}
                  />
                </div>

                {/* Model (Vehicle) / Product Serial Number (Trailer) / Facility Category (Storage) */}
                <div className="space-y-2">
                  <Label>{isStorage ? 'Facility Category *' : (isTrailer ? 'Product Serial Number' : 'Model')}</Label>
                  {isStorage ? (
                    <Select
                      value={vehicleOwnerInfo.model}
                      onValueChange={(v) => setVehicleOwnerInfo({ ...vehicleOwnerInfo, model: v })}
                    >
                      <SelectTrigger data-testid="facility-category">
                        <SelectValue placeholder="Select facility category" />
                      </SelectTrigger>
                      <SelectContent>
                        {FACILITY_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={vehicleOwnerInfo.model}
                      onChange={(e) => setVehicleOwnerInfo({ ...vehicleOwnerInfo, model: e.target.value })}
                      placeholder={isTrailer ? 'Enter serial number' : 'Corolla, XF, etc.'}
                      className={isTrailer ? 'font-mono uppercase' : ''}
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Year of Manufacture</Label>
                  <Input
                    type="number"
                    value={vehicleOwnerInfo.year}
                    onChange={(e) => setVehicleOwnerInfo({ ...vehicleOwnerInfo, year: e.target.value })}
                    placeholder="2020"
                    min="1990"
                    max={new Date().getFullYear()}
                  />
                </div>

                {/* Chassis Number (Vehicle/Trailer) / Installation Position (Storage) */}
                <div className="space-y-2">
                  <Label>{isStorage ? 'Installation Position *' : 'Chassis Number'}</Label>
                  {isStorage ? (
                    <Select
                      value={vehicleOwnerInfo.chassis_number}
                      onValueChange={(v) => setVehicleOwnerInfo({ ...vehicleOwnerInfo, chassis_number: v })}
                    >
                      <SelectTrigger data-testid="storage-installation-position">
                        <SelectValue placeholder="Select installation position" />
                      </SelectTrigger>
                      <SelectContent>
                        {STORAGE_INSTALLATION_POSITIONS.map((pos) => (
                          <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={vehicleOwnerInfo.chassis_number}
                      onChange={(e) => setVehicleOwnerInfo({ ...vehicleOwnerInfo, chassis_number: e.target.value.toUpperCase() })}
                      placeholder="Enter chassis number"
                      className="font-mono uppercase"
                    />
                  )}
                </div>

                {/* Engine Number (Vehicle) / Container Number (Trailer) / User Name (Storage) */}
                <div className="space-y-2">
                  <Label>{isStorage ? 'User Name' : (isTrailer ? 'Container Number' : 'Engine Number')}</Label>
                  <Input
                    value={vehicleOwnerInfo.engine_number}
                    onChange={(e) => setVehicleOwnerInfo({ ...vehicleOwnerInfo, engine_number: isStorage ? e.target.value : e.target.value.toUpperCase() })}
                    placeholder={isStorage ? 'Enter user/operator name' : (isTrailer ? 'Enter container number' : 'Enter engine number')}
                    className={isStorage ? '' : 'font-mono uppercase'}
                  />
                </div>

                {/* Mileage (Vehicle) / Capacity m³ (Trailer/Storage) */}
                <div className="space-y-2">
                  <Label>{isSpecialType ? 'Capacity (m³)' : 'Mileage (km)'}</Label>
                  <Input
                    type="number"
                    value={vehicleOwnerInfo.mileage}
                    onChange={(e) => setVehicleOwnerInfo({ ...vehicleOwnerInfo, mileage: e.target.value })}
                    placeholder={isSpecialType ? '500' : '45000'}
                    step={isSpecialType ? '0.1' : '1'}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Location / Region *</Label>
                  <Select
                    value={vehicleOwnerInfo.region}
                    onValueChange={(v) => setVehicleOwnerInfo({ ...vehicleOwnerInfo, region: v })}
                  >
                    <SelectTrigger data-testid="region">
                      <SelectValue placeholder="Select region" />
                    </SelectTrigger>
                    <SelectContent>
                      {(config.regions && config.regions.length > 0 ? config.regions : DEFAULT_REGIONS).map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: CNG Cylinder Information */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Cylinder className="h-5 w-5 text-primary" />
                  Step 3: CNG Cylinder Information
                </CardTitle>
                <CardDescription>
                  {cylinders.length} of {config.max_cylinders} cylinders
                </CardDescription>
              </div>
              <Button onClick={handleAddCylinder} size="sm" data-testid="add-cylinder">
                <Plus className="mr-2 h-4 w-4" />
                Add Cylinder
              </Button>
            </div>
            {/* Bulk entry tip */}
            {cylinders.length === 1 && (
              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
                  <CopyPlus className="h-4 w-4 flex-shrink-0" />
                  <span>
                    <strong>Tip:</strong> For multiple identical cylinders, fill in the first cylinder's details, then use <strong>"Add Similar"</strong> to quickly duplicate specifications. Only serial numbers need to be unique.
                  </span>
                </p>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Warning banner if any cylinders are expired */}
            {expiredCylinders.length > 0 && (
              <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-red-800 dark:text-red-300">Expired Cylinder(s) Detected</h4>
                    <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                      {expiredCylinders.length} cylinder(s) have expired before the inspection date. 
                      Expired cylinders must be replaced before proceeding with the inspection.
                      Certificate and QR code generation will be blocked.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {cylinders.map((cyl, idx) => {
              const isExpired = expiredCylinders.includes(idx);
              const inspDate = inspectionDetails.inspection_date ? parseISO(inspectionDetails.inspection_date) : new Date();
              const cylExpiry = cyl.expiry_date ? parseISO(cyl.expiry_date) : null;
              const isExpiredOnInspection = cylExpiry && cylExpiry < inspDate;
              const isCrossRecordDup = !!cylinderDupByRow[idx];

              return (
              <div key={idx} className={cn(
                "p-4 border rounded-lg space-y-4",
                isExpired || isExpiredOnInspection
                  ? "bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-700 ring-2 ring-red-400 dark:ring-red-600"
                  : isCrossRecordDup
                    ? "bg-amber-50 dark:bg-amber-950/20 border-amber-400 dark:border-amber-700 ring-2 ring-amber-300"
                    : cyl.isDuplicate
                      ? "bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800" 
                    : "bg-muted/20"
              )} data-testid={`cylinder-card-${idx}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold flex items-center gap-2">
                      <Cylinder className={cn("h-4 w-4", isExpiredOnInspection && "text-red-600")} />
                      Cylinder #{idx + 1}
                    </h4>
                    {isExpiredOnInspection && (
                      <Badge variant="destructive" className="bg-red-600 text-white animate-pulse">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        EXPIRED
                      </Badge>
                    )}
                    {cyl.isDuplicate && !isExpiredOnInspection && (
                      <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                        <Copy className="h-3 w-3 mr-1" />
                        Duplicated
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {/* Add Similar Cylinder Button */}
                    {cylinders.length < config.max_cylinders && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddSimilarCylinder(idx)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
                        title="Add similar cylinder with same specifications"
                        data-testid={`duplicate-cylinder-${idx}`}
                      >
                        <CopyPlus className="h-4 w-4 mr-1" />
                        Add Similar
                      </Button>
                    )}
                    {cylinders.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveCylinder(idx)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`remove-cylinder-${idx}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Quick info bar for duplicated cylinders */}
                {cyl.isDuplicate && cyl.manufacturer && (
                  <div className="text-xs text-blue-600 dark:text-blue-400 bg-blue-100/50 dark:bg-blue-900/30 px-3 py-1.5 rounded-md flex items-center gap-2">
                    <span>Pre-filled: Type {cyl.cylinder_type} | {cyl.manufacturer} | {cyl.service_pressure} MPa | {cyl.life_span} years</span>
                  </div>
                )}

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2 lg:col-span-3">
                    <Label className={cyl.isDuplicate && !cyl.serial_number ? "text-amber-600 dark:text-amber-400" : ""}>
                      Serial Number * {cyl.isDuplicate && !cyl.serial_number && <span className="text-xs">(Enter unique serial)</span>}
                    </Label>
                    <div className="relative max-w-md">
                      <Input
                        value={cyl.serial_number}
                        onChange={(e) => updateCylinder(idx, 'serial_number', e.target.value.toUpperCase())}
                        placeholder={cyl.isDuplicate ? "Enter unique serial number" : "CYL-001"}
                        className={cn(
                          "font-mono uppercase pr-10",
                          cylinderDupByRow[idx] && "border-amber-500 ring-2 ring-amber-200 focus-visible:ring-amber-300 bg-amber-50/40",
                          cyl.isDuplicate && !cyl.serial_number && !cylinderDupByRow[idx] && "border-amber-400 focus:border-amber-500 bg-amber-50 dark:bg-amber-950/30"
                        )}
                        data-testid={`cylinder-${idx}-serial`}
                        autoFocus={cyl.isDuplicate && !cyl.serial_number}
                        aria-invalid={!!cylinderDupByRow[idx]}
                      />
                      {cylinderChecking && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                    </div>
                    {cylinderDupByRow[idx] && (
                      <DuplicateAlert
                        field="cylinder_serial"
                        value={cylinderDupByRow[idx].value}
                        existingRecord={cylinderDupByRow[idx].existing_record}
                        sameFacility={cylinderDupByRow[idx].same_facility}
                        inFormLocation={cylinderDupByRow[idx].inFormLocation}
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select
                      value={cyl.cylinder_type}
                      onValueChange={(v) => updateCylinder(idx, 'cylinder_type', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CYLINDER_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Manufacturer</Label>
                    <Input
                      list={`cyl-mfr-list-${idx}`}
                      value={cyl.manufacturer}
                      onChange={(e) => updateCylinder(idx, 'manufacturer', e.target.value)}
                      placeholder="Select or type manufacturer"
                      data-testid={`cyl-manufacturer-${idx}`}
                    />
                    <datalist id={`cyl-mfr-list-${idx}`}>
                      {CYLINDER_MANUFACTURERS.map((m) => (
                        <option key={m} value={m} />
                      ))}
                    </datalist>
                  </div>

                  {/* Installation Position - NOT shown for Trailers/Storage */}
                  {!isSpecialType && (
                    <div className="space-y-2">
                      <Label>Installation Position</Label>
                      <Select
                        value={cyl.installation_position}
                        onValueChange={(v) => updateCylinder(idx, 'installation_position', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CYLINDER_POSITIONS.map((p) => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Service Pressure *</Label>
                    <Select
                      value={cyl.service_pressure}
                      onValueChange={(v) => updateCylinder(idx, 'service_pressure', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select pressure" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_PRESSURES.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Testing Pressure - Auto-calculated from Service Pressure (read-only) */}
                  <div className="space-y-2">
                    <Label>Testing Pressure (Auto)</Label>
                    <Input
                      type="text"
                      value={cyl.testing_pressure ? `${cyl.testing_pressure} MPa` : ''}
                      disabled
                      className="bg-muted"
                      placeholder="Auto-calculated"
                    />
                    <p className="text-xs text-muted-foreground">
                      Calculated as 1.5× Service Pressure
                    </p>
                  </div>

                  {/* Water Volume - Only for vehicles (not trailer/storage) */}
                  {!isSpecialType && (
                    <div className="space-y-2">
                      <Label>Water Volume (L)</Label>
                      <Input
                        type="number"
                        value={cyl.water_volume}
                        onChange={(e) => updateCylinder(idx, 'water_volume', e.target.value)}
                        placeholder="110"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Manufacturing Date *</Label>
                    <Input
                      type="date"
                      value={cyl.manufacturing_date}
                      onChange={(e) => updateCylinder(idx, 'manufacturing_date', e.target.value)}
                      data-testid={`cylinder-${idx}-manufacturing-date`}
                    />
                    <p className="text-xs text-muted-foreground">Required for certificate generation</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Life Span</Label>
                    <Select
                      value={cyl.life_span}
                      onValueChange={(v) => updateCylinder(idx, 'life_span', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LIFE_SPANS.map((l) => (
                          <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className={isExpiredOnInspection ? "text-red-600 dark:text-red-400" : ""}>
                      Expiry Date (Auto) {isExpiredOnInspection && <span className="text-xs font-normal">(EXPIRED)</span>}
                    </Label>
                    <div className={cn(
                      "flex items-center gap-2 h-10 px-3 border rounded-md",
                      isExpiredOnInspection 
                        ? "bg-red-100 dark:bg-red-950/50 border-red-400 dark:border-red-700" 
                        : "bg-muted/50"
                    )}>
                      {isExpiredOnInspection && <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />}
                      <span className={cn(
                        "text-sm",
                        isExpiredOnInspection && "text-red-700 dark:text-red-300 font-medium"
                      )}>
                        {cyl.expiry_date ? format(parseISO(cyl.expiry_date), 'dd MMM yyyy') : 'Set manufacturing date'}
                      </span>
                    </div>
                    {isExpiredOnInspection && (
                      <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                        This cylinder expired before the inspection date and must be replaced
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
          </CardContent>
        </Card>
      )}

      {/* Step 4: Inspection Results */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Step 4: Inspection Results
            </CardTitle>
            <CardDescription>
              Record the inspection test results {isSpecialType && `(${isTrailer ? 'Trailer' : 'Storage'} inspections require NDT test and file uploads)`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Mandatory Tests */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Mandatory Tests</h3>
              
              {/* Visual Inspection */}
              <div className="p-4 border rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="font-medium">Detailed Visual Inspection *</p>
                    <p className="text-sm text-muted-foreground">Check for physical damage, corrosion, dents</p>
                  </div>
                  <TestResultButtons
                    value={inspectionResults.visual_inspection}
                    onChange={(v) => setInspectionResults({ ...inspectionResults, visual_inspection: v })}
                    testId="visual-inspection"
                  />
                </div>
              </div>

              {/* Gas Leak Test - With file upload for Trailer/Storage */}
              <div className="p-4 border rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="font-medium">Gas Leak Test *</p>
                    <p className="text-sm text-muted-foreground">Check all connections and valves for leaks</p>
                  </div>
                  <TestResultButtons
                    value={inspectionResults.gas_leak_test}
                    onChange={(v) => {
                      setInspectionResults({ ...inspectionResults, gas_leak_test: v });
                      if (v === 'not_conducted' && isSpecialType) handleRemoveFile('leak_test_report');
                    }}
                    testId="gas-leak-test"
                  />
                </div>
                {/* File Upload for Leak Test - Trailer/Storage, only when test conducted */}
                {isSpecialType && inspectionResults.gas_leak_test && inspectionResults.gas_leak_test !== 'not_conducted' && (
                  <div className="mt-4 pt-4 border-t">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      Upload Leak Test Report / Certificate *
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">PDF or Image (JPEG, PNG) - Required for {isTrailer ? 'trailer' : 'storage'} inspections</p>
                    {testReportFiles.leak_test_report ? (
                      <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
                        <FileText className="h-4 w-4 text-primary" />
                        <span className="text-sm flex-1 truncate">{testReportFiles.leak_test_report.name}</span>
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveFile('leak_test_report')}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ) : (
                      <Input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => handleFileUpload('leak_test_report', e.target.files?.[0])}
                        disabled={uploading.leak_test_report}
                        className="cursor-pointer"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* NDT Test - Mandatory for Trailers/Storage */}
              {isSpecialType && (
                <div className="p-4 border rounded-lg border-amber-300 bg-amber-50/50">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="font-medium">Non-Destructive Test (NDT) *</p>
                      <p className="text-sm text-muted-foreground">Ultrasonic or radiographic testing (mandatory for {isTrailer ? 'trailers' : 'storage'})</p>
                    </div>
                    <TestResultButtons
                      value={inspectionResults.ndt_test}
                      onChange={(v) => {
                        setInspectionResults({ ...inspectionResults, ndt_test: v });
                        if (v === 'not_conducted') handleRemoveFile('ndt_test_report');
                      }}
                      testId="ndt-test-mandatory"
                    />
                  </div>
                  {/* File Upload for NDT Test - Trailer/Storage, only when test conducted */}
                  {inspectionResults.ndt_test && inspectionResults.ndt_test !== 'not_conducted' && (
                  <div className="mt-4 pt-4 border-t border-amber-200">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      Upload NDT Test Report / Certificate *
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">PDF or Image (JPEG, PNG) - Required for {isTrailer ? 'trailer' : 'storage'} inspections</p>
                    {testReportFiles.ndt_test_report ? (
                      <div className="flex items-center gap-2 p-2 bg-white rounded-md border">
                        <FileText className="h-4 w-4 text-primary" />
                        <span className="text-sm flex-1 truncate">{testReportFiles.ndt_test_report.name}</span>
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveFile('ndt_test_report')}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ) : (
                      <Input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => handleFileUpload('ndt_test_report', e.target.files?.[0])}
                        disabled={uploading.ndt_test_report}
                        className="cursor-pointer bg-white"
                      />
                    )}
                  </div>
                  )}
                </div>
              )}
            </div>

            <Separator />

            {/* Optional Tests (or Pressure Test for Trailers/Storage) */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                {isSpecialType ? 'Additional Tests' : 'Optional Tests'}
              </h3>
              
              {/* Pressure Test */}
              <div className="p-4 border rounded-lg bg-muted/20">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="font-medium">Pressure Test</p>
                    <p className="text-sm text-muted-foreground">
                      Verify pressure holding capacity {isSpecialType ? '(file upload required if conducted)' : '(optional)'}
                    </p>
                  </div>
                  <TestResultButtons
                    value={inspectionResults.pressure_test}
                    onChange={(v) => {
                      setInspectionResults({ ...inspectionResults, pressure_test: v });
                      if (v === 'not_conducted' && isSpecialType) handleRemoveFile('pressure_test_report');
                    }}
                    testId="pressure-test"
                  />
                </div>
                {/* File Upload for Pressure Test - Trailer/Storage, only when test conducted */}
                {isSpecialType && inspectionResults.pressure_test && inspectionResults.pressure_test !== 'not_conducted' && (
                  <div className="mt-4 pt-4 border-t">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      Upload Pressure Test Report / Certificate *
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">PDF or Image (JPEG, PNG) - Required when test is conducted</p>
                    {testReportFiles.pressure_test_report ? (
                      <div className="flex items-center gap-2 p-2 bg-white rounded-md border">
                        <FileText className="h-4 w-4 text-primary" />
                        <span className="text-sm flex-1 truncate">{testReportFiles.pressure_test_report.name}</span>
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveFile('pressure_test_report')}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ) : (
                      <Input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => handleFileUpload('pressure_test_report', e.target.files?.[0])}
                        disabled={uploading.pressure_test_report}
                        className="cursor-pointer"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* NDT Test - Optional for non-trailers/storage */}
              {!isSpecialType && (
                <div className="p-4 border rounded-lg bg-muted/20">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="font-medium">Non-Destructive Test (NDT)</p>
                      <p className="text-sm text-muted-foreground">Ultrasonic or radiographic testing (optional)</p>
                    </div>
                    <TestResultButtons
                      value={inspectionResults.ndt_test}
                      onChange={(v) => setInspectionResults({ ...inspectionResults, ndt_test: v })}
                      testId="ndt-test-optional"
                    />
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* Overall Result */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Overall Result *</h3>
              <RadioGroup
                value={inspectionResults.overall_result}
                onValueChange={(v) => setInspectionResults({ ...inspectionResults, overall_result: v })}
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                {OVERALL_RESULTS.map((result) => (
                  <div key={result.value}>
                    <RadioGroupItem
                      value={result.value}
                      id={result.value}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={result.value}
                      className={`flex items-center justify-center rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent cursor-pointer transition-all peer-data-[state=checked]:border-2 ${
                        result.value === 'approved' ? 'peer-data-[state=checked]:border-emerald-500 peer-data-[state=checked]:bg-emerald-50' :
                        result.value === 'conditionally_approved' ? 'peer-data-[state=checked]:border-amber-500 peer-data-[state=checked]:bg-amber-50' :
                        'peer-data-[state=checked]:border-red-500 peer-data-[state=checked]:bg-red-50'
                      }`}
                    >
                      <div className={`h-3 w-3 rounded-full mr-2 ${result.color}`} />
                      {result.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                value={inspectionResults.notes}
                onChange={(e) => setInspectionResults({ ...inspectionResults, notes: e.target.value })}
                placeholder="Any additional observations or recommendations..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Inspector Authentication & Review */}
      {step === 5 && (
        <div className="space-y-6">
          {/* Inspector Authentication Card */}
          <Card className="border-emerald-200 bg-emerald-50/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-emerald-600" />
                Step 5: Inspector Authentication
              </CardTitle>
              <CardDescription>
                This information is automatically filled and cannot be edited
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Inspector Name</Label>
                  <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-white">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{user?.full_name || 'N/A'}</span>
                    <Lock className="h-3 w-3 text-muted-foreground ml-auto" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Inspector ID / License No</Label>
                  <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-white">
                    <span className="font-mono">{user?.certificate_number || 'N/A'}</span>
                    <Lock className="h-3 w-3 text-muted-foreground ml-auto" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Digital Signature</Label>
                  <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-white">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    <span className="text-emerald-600">Digitally Verified</span>
                    <Lock className="h-3 w-3 text-muted-foreground ml-auto" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Inspection Date</Label>
                  <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-white">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{format(parseISO(inspectionDetails.inspection_date), 'dd MMMM yyyy')}</span>
                    <Lock className="h-3 w-3 text-muted-foreground ml-auto" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Review */}
          <Card>
            <CardHeader>
              <CardTitle>Review Summary</CardTitle>
              <CardDescription>Please verify all information before submitting</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Inspection Details Summary */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Inspection Details</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">Type</p>
                    <p className="font-medium text-sm">{INSPECTION_TYPES.find(t => t.value === inspectionDetails.inspection_type)?.label}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Valid From</p>
                    <p className="font-medium text-sm">{format(parseISO(inspectionDetails.inspection_date), 'dd MMM yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Valid To</p>
                    <p className="font-medium text-sm">{format(parseISO(validTo), 'dd MMM yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Validity</p>
                    <p className="font-medium text-sm">{inspectionDetails.validity_months} Months</p>
                  </div>
                </div>
              </div>

              {/* Vehicle & Owner Summary */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Vehicle & Owner</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">Owner</p>
                    <p className="font-medium text-sm">{vehicleOwnerInfo.owner_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Registration</p>
                    <p className="font-mono font-medium text-sm">{vehicleOwnerInfo.registration_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Type</p>
                    <p className="font-medium text-sm">{getRecordTypeEmoji(facilityType)} {getRecordTypeLabel(facilityType)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Make / Model</p>
                    <p className="font-medium text-sm">{vehicleOwnerInfo.make} {vehicleOwnerInfo.model}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Year</p>
                    <p className="font-medium text-sm">{vehicleOwnerInfo.year || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Region</p>
                    <p className="font-medium text-sm">{vehicleOwnerInfo.region}</p>
                  </div>
                </div>
              </div>

              {/* Cylinders Summary */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                  CNG Cylinders ({cylinders.length})
                </h4>
                <div className="space-y-2">
                  {cylinders.map((cyl, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">#{idx + 1}</Badge>
                        <span className="font-mono text-sm">{cyl.serial_number}</span>
                        <span className="text-sm text-muted-foreground">Type {cyl.cylinder_type}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">{cyl.service_pressure} MPa</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Results Summary */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Test Results</h4>
                <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    {(() => {
                      const renderTest = (label, value) => {
                        const labelText = value === 'pass' ? 'Passed' : value === 'fail' ? 'Failed' : 'Not Conducted';
                        const Icon = value === 'pass' ? CheckCircle : value === 'fail' ? XCircle : MinusCircle;
                        const color = value === 'pass' ? 'text-emerald-500' : value === 'fail' ? 'text-red-500' : 'text-gray-500';
                        return (
                          <div className="flex items-center gap-2">
                            <Icon className={`h-4 w-4 ${color}`} />
                            <span className="text-sm">{label}: {labelText}</span>
                          </div>
                        );
                      };
                      return (
                        <>
                          {renderTest('Visual Inspection', inspectionResults.visual_inspection)}
                          {renderTest('Gas Leak Test', inspectionResults.gas_leak_test)}
                          {inspectionResults.pressure_test && renderTest('Pressure Test', inspectionResults.pressure_test)}
                          {inspectionResults.ndt_test && renderTest('NDT Test', inspectionResults.ndt_test)}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Overall Result Banner */}
              <div className={`p-6 rounded-lg text-center ${
                inspectionResults.overall_result === 'approved' ? 'bg-emerald-100' :
                inspectionResults.overall_result === 'conditionally_approved' ? 'bg-amber-100' :
                'bg-red-100'
              }`}>
                <p className={`text-2xl font-bold uppercase ${
                  inspectionResults.overall_result === 'approved' ? 'text-emerald-700' :
                  inspectionResults.overall_result === 'conditionally_approved' ? 'text-amber-700' :
                  'text-red-700'
                }`}>
                  STATUS: {inspectionResults.overall_result.replace('_', ' ')}
                </p>
                <p className="text-sm mt-2 text-muted-foreground">
                  {inspectionResults.overall_result === 'approved' && 'Certificate will be generated upon submission'}
                  {inspectionResults.overall_result === 'conditionally_approved' && 'Certificate will be generated with conditions noted'}
                  {inspectionResults.overall_result === 'rejected' && 'No certificate will be generated'}
                </p>
              </div>

              {/* Warning for rejected */}
              {inspectionResults.overall_result === 'rejected' && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-700">Inspection Rejected</p>
                    <p className="text-sm text-red-600">
                      This inspection will be recorded but no certificate will be issued.
                      The vehicle owner will need to address the issues and schedule a re-inspection.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={step === 1}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Previous
        </Button>

        {step < 5 ? (
          <Button onClick={handleNext} disabled={validating}>
            {validating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Validating...
              </>
            ) : (
              <>
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="submit-inspection"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="mr-2 h-4 w-4" />
                Submit Inspection
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
